// Reusable "simple master data" CRUD page (name-only)
import { useEffect, useState } from "react";
import { PageHeader } from "../../components/common/PageHeader";
import { DataTable } from "../../components/common/DataTable";
import { FormModal } from "../../components/common/FormModal";
import { StatusBadge } from "../../components/common/StatusBadge";
import { ConfirmDialog } from "../../components/common/ConfirmDialog";
import { TextField } from "../../components/common/Form";
import { Button } from "@/components/ui/button";
import { Plus, Pencil, Power, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useCan } from "../../hooks/useCan";

export const SimpleMasterPage = ({ title, subtitle, service, idKey, nameField, nameLabel, testIdPrefix }) => {
  const can = useCan();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => { setLoading(true); service.list().then(setRows).finally(() => setLoading(false)); };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const openCreate = () => { setEditingId(null); setName(""); setOpen(true); };
  const openEdit = (r) => { setEditingId(r[idKey]); setName(r[nameField] || ""); setOpen(true); };

  const submit = async () => {
    if (!name.trim()) return toast.error("Name is required");
    setSaving(true);
    try {
      if (editingId) {
        if (!service.update) throw new Error("Update not supported by backend");
        await service.update(editingId, name.trim());
        toast.success("Updated");
      } else {
        await service.create(name.trim());
        toast.success("Created");
      }
      setOpen(false); setName(""); setEditingId(null); load();
    } catch (e) { toast.error(e?.response?.data?.message || e.message || "Failed"); }
    finally { setSaving(false); }
  };

  const toggle = async (r) => {
    if (!service.setStatus) return toast.error("Status change not supported by backend");
    try {
      await service.setStatus(r[idKey], r.status === "ACTIVE" ? "INACTIVE" : "ACTIVE");
      toast.success("Status updated"); load();
    } catch (e) { toast.error(e?.response?.data?.message || "Failed"); }
  };

  const remove = async () => {
    if (!service.remove) return toast.error("Delete not supported by backend");
    setBusy(true);
    try {
      await service.remove(confirmDelete[idKey]);
      toast.success("Deleted");
      setConfirmDelete(null); load();
    } catch (e) { toast.error(e?.response?.data?.message || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div>
      <PageHeader
        title={title}
        subtitle={subtitle}
        actions={can.create && <Button onClick={openCreate} className="bg-navy-800 hover:bg-navy-900 text-white" data-testid={`${testIdPrefix}-add`}><Plus className="h-4 w-4 mr-2" />New</Button>}
      />
      <DataTable
        loading={loading}
        rows={rows.map((r) => ({ ...r, key: r[idKey] }))}
        testId={`${testIdPrefix}-table`}
        searchable
        searchKeys={[idKey, nameField, "status"]}
        searchPlaceholder={`Search ${nameLabel.toLowerCase()}…`}
        columns={[
          { key: idKey, header: "ID" },
          { key: nameField, header: nameLabel },
          { key: "status", header: "Status", render: (r) => <StatusBadge value={r.status} /> },
          {
            key: "actions",
            header: "",
            render: (r) => can.modify ? (
              <div className="flex gap-2">
                {service.update && (
                  <Button variant="outline" size="sm" onClick={() => openEdit(r)} data-testid={`${testIdPrefix}-edit-${r[idKey]}`} title="Edit"><Pencil className="h-3.5 w-3.5" /></Button>
                )}
                {service.setStatus && (
                  <Button variant="outline" size="sm" onClick={() => toggle(r)} data-testid={`${testIdPrefix}-toggle-${r[idKey]}`} title={r.status === "ACTIVE" ? "Deactivate" : "Activate"}><Power className="h-3.5 w-3.5" /></Button>
                )}
                {/* {service.remove && (
                  <Button variant="outline" size="sm" onClick={() => setConfirmDelete(r)} data-testid={`${testIdPrefix}-delete-${r[idKey]}`} title="Delete" className="text-rose-600 hover:bg-rose-50"><Trash2 className="h-3.5 w-3.5" /></Button>
                )} */}
              </div>
            ) : <span className="text-slate-300 text-xs">—</span>,
          },
        ]}
      />
      <FormModal
        open={open}
        onOpenChange={setOpen}
        title={editingId ? `Edit ${nameLabel.toLowerCase()}` : `New ${nameLabel.toLowerCase()}`}
        onSubmit={submit}
        loading={saving}
        submitText={editingId ? "Save" : "Create"}
      >
        <TextField label={nameLabel} required value={name} onChange={setName} testId={`${testIdPrefix}-name`} />
      </FormModal>
      <ConfirmDialog
        open={!!confirmDelete}
        onOpenChange={(v) => !v && setConfirmDelete(null)}
        title={`Delete ${nameLabel.toLowerCase()}?`}
        description={confirmDelete ? `“${confirmDelete[nameField]}” will be marked INACTIVE and hidden from operational lists.` : ""}
        confirmText="Delete"
        danger
        loading={busy}
        onConfirm={remove}
      />
    </div>
  );
};
